/**
 * Content Schema 内容渲染器
 * 用于渲染只读的编辑器内容
 * ONLY supports Content Schema format
 */

import React from "react";
import type {
  SerializedActionItemNode,
  SerializedChecklistNode,
  SerializedDecisionNode,
  SerializedPriceTrendNode,
  SerializedSelfContractNode,
} from "@/components/Editor/nodes/types";
import { NODE_TYPE_ENTITY, NODE_TYPE_INVESTMENT, NODE_TYPE_STRUCTURED } from "@/constants/lexical";
import { cn } from "@/lib/utils";
import { getTagById, getTagColorClassByTagId } from "@/services/TagService";
import { ActionItemView } from "./ActionItemView";
import { ChecklistView } from "./ChecklistView";
import { CodeBlock } from "./CodeBlock";
import { DecisionView } from "./DecisionView";
import { PriceTrendView } from "./PriceTrendView";
import { SelfContractView } from "./SelfContractView";
import { Tag } from "./Tag";
import { Ticker } from "./Ticker";

interface LexicalContentProps {
  content: string; // Content Schema JSON 字符串
  className?: string;
}

// Content Schema 节点类型
interface ContentSchemaNode {
  type: string;
  version?: number;
  text?: string;
  format?: number;
  children?: ContentSchemaNode[];
  [key: string]: unknown; // 允许其他自定义字段
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

/**
 * 渲染单个 Content Schema 节点
 * Content Schema format: Fields are directly on node object (no "data" wrapper)
 */
function renderNode(node: ContentSchemaNode, index: number): React.ReactNode {
  const { type, children, text, format, ...restData } = node;
  const rest = restData as Record<string, unknown>;

  // Decision 节点
  if (type === NODE_TYPE_INVESTMENT.DECISION) {
    return <DecisionView key={index} node={restData as unknown as SerializedDecisionNode} />;
  }

  // Checklist 节点
  if (type === NODE_TYPE_STRUCTURED.CHECKLIST) {
    return <ChecklistView key={index} data={restData as unknown as SerializedChecklistNode} />;
  }

  // 股价走势节点
  if (type === NODE_TYPE_INVESTMENT.PRICE_TREND) {
    return <PriceTrendView key={index} data={restData as unknown as SerializedPriceTrendNode} />;
  }

  // 行动项节点
  if (type === "action-item") {
    return <ActionItemView key={index} data={restData as unknown as SerializedActionItemNode} />;
  }

  // Tag 节点
  if (type === NODE_TYPE_ENTITY.TAG) {
    const tagId = asString(rest.tagId);
    const tagInfo = getTagById(tagId);
    if (tagInfo) {
      const colorClass = getTagColorClassByTagId(tagId);
      return (
        <Tag
          key={index}
          data-tag={tagId}
          className={`inline-flex items-center px-1.5 py-0.5 rounded hover:opacity-80 transition-opacity cursor-pointer select-none mr-1 ${colorClass}`}
        >
          #{tagInfo.displayName}
        </Tag>
      );
    }
    // Fallback for legacy tags without tagId
    const legacyName = asString(rest.name);
    if (legacyName) {
      return (
        <Tag
          key={index}
          className={`inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 hover:opacity-80 transition-opacity cursor-pointer select-none mr-1`}
        >
          #{legacyName}
        </Tag>
      );
    }
  }

  // Self-Contract 节点
  if (type === NODE_TYPE_INVESTMENT.SELF_CONTRACT) {
    return <SelfContractView key={index} node={restData as unknown as SerializedSelfContractNode} />;
  }

  // Stock/Ticker 节点
  if (type === NODE_TYPE_ENTITY.STOCK) {
    const symbol = asString(rest.symbol) || text || "";
    return (
      <Ticker
        key={index}
        data-ticker={symbol}
        variant="lexical"
        showDollarSign={false}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-50 text-green-700 hover:bg-green-100 transition-colors cursor-pointer select-none mr-1"
      >
        ${symbol}
      </Ticker>
    );
  }

  // 图片节点
  if (type === NODE_TYPE_ENTITY.IMAGE) {
    const src = asString(rest.src);
    const altText = asString(rest.altText);
    const filename = asString(rest.filename);
    const width = asNumber(rest.width);
    const height = asNumber(rest.height);
    const isAttachment = src.startsWith("attachments/");

    let imageUrl = src;
    if (isAttachment) {
      // 从 resource name 提取 uid (attachments/{uid})
      const uid = src.replace("attachments/", "");
      // 需要转换为：/file/attachments/{uid}/{filename}
      const filepath = filename || altText || "file";
      imageUrl = `/file/attachments/${uid}/${filepath}`;
    }

    return (
      <div key={index} className="inline-block my-2">
        <img
          src={imageUrl}
          alt={altText || "image"}
          className="rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          style={{
            width: width ? `${width}px` : "auto",
            height: height ? `${height}px` : "auto",
            maxWidth: width ? undefined : "100%",
            maxHeight: height ? undefined : "384px",
          }}
        />
      </div>
    );
  }

  // 段落节点
  if (type === "paragraph") {
    return (
      <p key={index} className="mb-3 last:mb-0 break-all">
        {children && children.map((child, i) => renderNode(child, i))}
      </p>
    );
  }

  // 列表节点
  if (type === "list") {
    const listType = asString(rest.listType);
    if (listType === "bullet" || listType === "check") {
      return (
        <ul key={index} className="list-disc list-inside mb-3 pl-4">
          {children && children.map((child, i) => renderNode(child, i))}
        </ul>
      );
    }
    // numbered list
    return (
      <ol key={index} className="list-decimal list-inside mb-3 pl-4">
        {children && children.map((child, i) => renderNode(child, i))}
      </ol>
    );
  }

  // 列表项节点
  if (type === "listitem") {
    const checked = rest.checked === true || rest.checked === false ? (rest.checked as boolean) : undefined;

    // Check if this listitem only contains a nested list (no text content)
    const hasOnlyNestedList = children && children.length === 1 && (children[0] as ContentSchemaNode).type === "list";

    return (
      <li key={index} className={cn("mb-1", checked !== undefined && "list-none flex items-start gap-2", hasOnlyNestedList && "list-none")}>
        {checked !== undefined && <input type="checkbox" checked={checked} readOnly className="mt-1 cursor-pointer" />}
        <span className={checked !== undefined ? "flex-1" : ""}>
          {children && children.length > 0 ? (
            children.map((child, i) => renderNode(child, i))
          ) : (
            // Empty listitem: render zero-width space to maintain line height
            <span className="inline-block min-h-[1.5em]">&nbsp;</span>
          )}
        </span>
      </li>
    );
  }

  // 标题节点
  if (type === "heading") {
    const tagNumber = asNumber(rest.tag) || 1;
    const Tag = `h${tagNumber}` as keyof JSX.IntrinsicElements;
    return (
      <Tag
        key={index}
        className={cn(
          "scroll-m-20 font-bold",
          tagNumber === 1 && "text-3xl",
          tagNumber === 2 && "text-2xl",
          tagNumber === 3 && "text-xl",
          tagNumber === 4 && "text-lg",
          tagNumber === 5 && "text-base",
          tagNumber === 6 && "text-sm",
        )}
      >
        {children && children.map((child, i) => renderNode(child, i))}
      </Tag>
    );
  }

  // 引用节点
  if (type === "quote") {
    return (
      <blockquote key={index} className="border-l-4 border-border pl-4 italic text-muted-foreground mb-3">
        {children && children.map((child, i) => renderNode(child, i))}
      </blockquote>
    );
  }

  // 代码块节点
  if (type === "code") {
    const code = node.children?.[0]?.text || "";
    const language = asString(rest.language);
    return (
      <CodeBlock key={index} className={`language-${language}`}>
        <code className={`language-${language}`}>{code}</code>
      </CodeBlock>
    );
  }

  // 水平线节点
  if (type === "horizontalrule") {
    return <hr key={index} className="my-4 border-border" />;
  }

  // 链接节点
  if (type === "link") {
    return (
      <a key={index} href={asString(rest.url)} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
        {children && children.map((child, i) => renderNode(child, i))}
      </a>
    );
  }

  // 文本节点
  if (type === "text" || type === "linebreak") {
    if (type === "linebreak") {
      return <br key={index} />;
    }

    let content: React.ReactNode = text || "";

    // 处理格式
    if (format) {
      if (format & 1) {
        // Bold
        content = <strong>{content}</strong>;
      }
      if (format & 2) {
        // Italic
        content = <em>{content}</em>;
      }
      if (format & 4) {
        // Strikethrough
        content = <s>{content}</s>;
      }
      if (format & 8) {
        // Underline
        content = <u>{content}</u>;
      }
      if (format & 16) {
        // Code
        content = <code className="px-1 py-0.5 rounded bg-muted text-sm font-mono">{content}</code>;
      }
    }

    return <React.Fragment key={index}>{content}</React.Fragment>;
  }

  // 未知节点类型，尝试渲染子节点
  if (children && children.length > 0) {
    return <React.Fragment key={index}>{children.map((child, i) => renderNode(child, i))}</React.Fragment>;
  }

  // 完全未知的节点，返回 null
  return null;
}

/**
 * Content Schema 内容渲染器主组件
 * ONLY supports Content Schema format: { type: "root", version: 1, children: [...] }
 */
const LexicalContent: React.FC<LexicalContentProps> = ({ content, className }) => {
  // 解析 Content Schema JSON
  let contentSchema: ContentSchemaNode | null = null;
  try {
    contentSchema = JSON.parse(content);
  } catch (error) {
    console.error("Failed to parse Content Schema:", error);
    return <div className={cn("text-destructive", className)}>内容解析失败：无效的 Content Schema JSON</div>;
  }

  // 验证数据结构
  if (!contentSchema || contentSchema.type !== "root") {
    return <div className={cn("text-destructive", className)}>内容格式错误：缺少 root 节点或不是 Content Schema 格式</div>;
  }

  // 渲染根节点的子节点
  const children = contentSchema.children || [];

  return <div className={cn("lexical-content", className)}>{children.map((child, index) => renderNode(child, index))}</div>;
};

export default LexicalContent;
